import React, { useState, useEffect } from "react";
import { MessageSquare, ThumbsUp, MoreHorizontal } from "lucide-react";
import { Modal, Input, Button, Popover } from "antd";
import { deleteData, getData, postData, putData } from "../../api/api";
import ComDateConverter from "./../../Components/ComDateConverter/ComDateConverter";
import { useStorage } from "../../hooks/useLocalStorage";
import { useParams } from "react-router-dom";

export default function ForumDetail() {
  // State quản lý chi tiết bài đăng
  const [post, setPost] = useState(null);
  
  // State quản lý nội dung bình luận
  const [cmt, setValueCmt] = useState("");
  
  // Hook lưu trữ thông tin người dùng
  const [use, setUse] = useStorage("use", null);
  
  // State quản lý chế độ chỉnh sửa bình luận
  const [editingComment, setEditingComment] = useState(null);
  const [newCommentContent, setNewCommentContent] = useState("");
  
  // State quản lý trả lời bình luận
  const [replyContent, setReplyContent] = useState("");
  const [editingReply, setEditingReply] = useState(null);
  const [newReplyContent, setNewReplyContent] = useState("");
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  
  // Lấy ID bài đăng từ URL
  const { id } = useParams();

  // State quản lý trạng thái loading cho các hành động
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [isPostingReply, setIsPostingReply] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isSavingReplyEdit, setIsSavingReplyEdit] = useState(false);

  // Hàm tải dữ liệu bài đăng chi tiết
  const callData = () => {
    getData(`posts/${id}`)
      .then((e) => {
        setPost(e.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Effect tải dữ liệu khi component mount hoặc ID thay đổi
  useEffect(() => {
    callData();
  }, [id]);

  // Hàm bắt đầu chỉnh sửa bình luận
  const handleEditClick = (comment) => {
    setEditingComment(comment);
    setNewCommentContent(comment.content);
  };

  // Hàm like bài đăng
  const callPostLike = () => {
    postData("/reactions", {
      postId: id,
      accountId: use.id, 
    })
      .then((e) => {
        callData(); // Tải lại dữ liệu để cập nhật số lượng like
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Kiểm tra người dùng hiện tại đã like chưa
  const userLiked = post?.reactions?.some(
    (reaction) => reaction.account.id === use.id
  );

  // Hàm lưu chỉnh sửa bình luận
  const handleSaveEdit = () => {
    if (newCommentContent.trim() === "") return;
    setIsSavingEdit(true); 
    putData(`/comments`, editingComment.id, { content: newCommentContent })
      .then((e) => {
        callData(); // Tải lại dữ liệu sau khi chỉnh sửa
        setEditingComment(null); // Đóng modal chỉnh sửa
        setNewCommentContent(""); // Đặt lại nội dung input
        setIsSavingEdit(false); 
      })
      .catch((error) => {
        console.log(error);
        setIsSavingEdit(false);
      });
  };

  // Hàm bắt đầu chỉnh sửa trả lời
  const handleEditReplyClick = (reply) => {
    setEditingReply(reply);
    setNewReplyContent(reply.content);
  };

  // Hàm lưu chỉnh sửa trả lời
  const handleSaveReplyEdit = () => {
    if (newReplyContent.trim() === "") return;
    setIsSavingReplyEdit(true);
    putData(`/comments`, editingReply.id, { content: newReplyContent })
      .then((e) => {
        callData(); // Tải lại dữ liệu sau khi chỉnh sửa
        setEditingReply(null); // Đóng modal chỉnh sửa
        setNewReplyContent(""); // Đặt lại nội dung input
        setIsSavingReplyEdit(false);
      })
      .catch((error) => {
        console.log(error);
        setIsSavingReplyEdit(false);
      });
  };

  // Hàm đăng bình luận
  const handlePostComment = () => {
    if (cmt) {
      setIsPostingComment(true);
      postData(`/comments`, {
        postId: id,
        content: cmt,
      })
        .then((e) => {
          setIsPostingComment(false);
          callData(); // Tải lại dữ liệu
          setValueCmt(""); // Đặt lại input bình luận
        })
        .catch((error) => {
          console.log(error);
          setIsPostingComment(false);
        });
    }
  };

  // Hàm đăng trả lời
  const handlePostReply = (commentId) => {
    if (replyContent.trim() === "") return;
    setIsPostingReply(true);
    postData(`/comments/reply`, {
      commentId: commentId,
      content: replyContent,
    })
      .then((e) => {
        callData(); // Tải lại dữ liệu
        setReplyContent(""); // Đặt lại input trả lời
        setReplyingToCommentId(null); // Đóng input trả lời
        setIsPostingReply(false);
      })
      .catch((error) => {
        console.log(error);
        setIsPostingReply(false);
      });
  };

  // Hiển thị loading nếu chưa có dữ liệu
  if (!post) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl font-semibold text-gray-600">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#dfe0e2]">
      <div className="max-w-4xl mx-auto bg-[#dfe0e2] p-2 sm:p-4">
        {/* Main post container - make it stack vertically on mobile */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* User info section - adjust width and centering */}
          <div className="w-full sm:w-32 flex-shrink-0">
            <div className="flex flex-row sm:flex-col items-center text-center gap-4 sm:gap-0">
              <img
                src={post?.createdByNavigation?.avatar}
                alt="User avatar"
                className="rounded-full mb-0 sm:mb-2 w-[60px] h-[60px] sm:w-[100px] sm:h-[100px]"
              />
              <div className="text-blue-600 font-medium">
                {post?.createdByNavigation?.firstName}{" "}
                {post?.createdByNavigation?.lastName}
              </div>
            </div>
          </div>

          {/* Post content section - adjust padding and spacing */}
          <div className="flex-1 bg-[#eaebec] p-2 sm:p-4">
            {/* Thời gian đăng */}
            <div className="flex items-center justify-between mb-4 text-sm text-gray-600 border-b-2 border-black">
              <div>
                <ComDateConverter>{post.createAt}</ComDateConverter>
              </div>
            </div>

            <div className="space-y-4">
              {/* Nội dung văn bản */}
              <p className="text-gray-900">{post.content}</p>

              {/* Hình ảnh đi kèm */}
              <div className="rounded-lg overflow-hidden">
                {post?.postImages &&
                  post?.postImages.map((data) => (
                    <img
                      key={data?.id}
                      src={data?.url}
                      alt="Post Image"
                      className="w-full"
                    />
                  ))}
              </div>

              {/* Phần like và comment (chỉ hiển thị khi đã đăng nhập) */}
              {use.id && (
                <div className="flex justify-between mt-4">
                  {/* Nút like */}
                  <button
                    onClick={callPostLike}
                    className={`flex items-center gap-2 p-2 rounded-md ${
                      userLiked
                        ? "text-blue-600"
                        : "text-gray-600 hover:text-blue-600"
                    }`}
                  >
                    <ThumbsUp className="w-5 h-5" />
                    {post?.reactions?.length}
                  </button>

                  {/* Số lượng bình luận */}
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    {post?.comments?.length}
                  </div>
                </div>
              )}
            </div>

            {/* Ô nhập bình luận (chỉ hiển thị khi đã đăng nhập) */}
            {use.id && (
              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "16px",
                  marginTop: "16px",
                }}
              >
                <textarea
                  placeholder="Add comment ..."
                  onChange={(e) => setValueCmt(e.target.value)}
                  value={cmt}
                  style={{
                    width: "100%",
                    minHeight: "80px",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                    marginBottom: "8px",
                  }}
                ></textarea>
                <button
                  onClick={handlePostComment}
                  disabled={isPostingComment}
                  className={`w-full ${
                    isPostingComment
                      ? "bg-gray-400"
                      : "bg-yellow-500 hover:bg-yellow-600"
                  } text-white rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50`}
                >
                  {isPostingComment ? "Posting..." : "Post Comment"}
                </button>
              </div>
            )}

            {/* Hiển thị danh sách bình luận */}
            {post?.comments?.map((comment) => (
              <div key={comment.id} className="p-2 sm:p-4 border-b last:border-b-0">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <img
                    src={comment.account.avatar}
                    alt={comment.account.firstName}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                  />
                  
                  {/* Comment content with adjusted spacing */}
                  <div className="flex-1 text-sm sm:text-base">
                    {/* Thông tin người bình luận và tùy chọn chỉnh sửa */}
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm">
                        {comment.account.firstName} {comment.account.lastName}
                      </h3>
                      {use.id === comment.account.id && (
                        <Popover
                          content={
                            <div>
                              <Button
                                onClick={() => handleEditClick(comment)}
                                type="link"
                              >
                                Edit
                              </Button>
                            </div>
                          }
                          trigger="click"
                        >
                          <MoreHorizontal className="w-5 h-5 cursor-pointer" />
                        </Popover>
                      )}
                    </div>

                    {/* Chế độ chỉnh sửa bình luận */}
                    {editingComment?.id === comment.id ? (
                      <div>
                        <textarea
                          value={newCommentContent}
                          onChange={(e) => setNewCommentContent(e.target.value)}
                          style={{
                            width: "100%",
                            minHeight: "60px",
                            padding: "8px",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                          }}
                        />
                        <div className="mt-2 flex gap-2">
                          <Button
                            type="primary"
                            onClick={handleSaveEdit}
                            disabled={isSavingEdit}
                          >
                            {isSavingEdit ? "Saving..." : "Save"}
                          </Button>
                          <Button
                            type="default"
                            onClick={() => setEditingComment(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm mt-1 whitespace-pre-line">
                          {comment.content}
                        </p>
                        <p className="text-sm text-gray-600">
                          <ComDateConverter time>
                            {comment.createAt}
                          </ComDateConverter>
                        </p>
                      </>
                    )}

                    {/* Ô nhập trả lời (chỉ hiển thị khi đã đăng nhập) */}
                    {use.id && replyingToCommentId === comment.id && (
                      <div className="mt-4">
                        <textarea
                          placeholder="Reply to this comment..."
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          style={{
                            width: "100%",
                            minHeight: "60px",
                            padding: "8px",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                            marginBottom: "8px",
                          }}
                        ></textarea>
                        <button
                          onClick={() => handlePostReply(comment.id)}
                          disabled={isPostingReply}
                          className={`w-full ${
                            isPostingReply
                              ? "bg-gray-400"
                              : "bg-yellow-500 hover:bg-yellow-600"
                          } text-white rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50`}
                        >
                          {isPostingReply ? "Replying..." : "Reply"}
                        </button>
                      </div>
                    )}

                    {use.id && (
                      <button
                        onClick={() => {
                          setReplyingToCommentId(comment.id);
                          setReplyContent("");
                        }}
                        className="text-blue-600 mt-2 text-sm"
                      >
                        Reply
                      </button>
                    )}

                    {/* Show replies */}
                    <div className="mt-2 flex-1">
                      {comment.inverseParentComment?.map((reply) => (
                        <div key={reply.id} className="mt-2 pl-2 sm:pl-6">
                          <div className="flex gap-2 sm:gap-4">
                            <img
                              src={comment?.account?.avatar}
                              alt={comment?.account?.firstName}
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                            />
                            <div>
                              <p className="text-sm  text-gray-600">
                                <div className="flex justify-between gap-10">
                                  <strong>
                                    {reply.account.firstName}{" "}
                                    {reply.account.lastName}
                                  </strong>
                                  {use.id === reply.account.id && (
                                    <Popover
                                      content={
                                        <div>
                                          <Button
                                            onClick={() =>
                                              handleEditReplyClick(reply)
                                            }
                                            type="link"
                                          >
                                            Edit Reply
                                          </Button>
                                        </div>
                                      }
                                      trigger="click"
                                    >
                                      <MoreHorizontal className="w-5 h-5 cursor-pointer" />
                                    </Popover>
                                  )}
                                </div>
                                {editingReply?.id === reply.id ? (
                                  <div>
                                    <textarea
                                      value={newReplyContent}
                                      onChange={(e) =>
                                        setNewReplyContent(e.target.value)
                                      }
                                      style={{
                                        width: "100%",
                                        minHeight: "60px",
                                        padding: "8px",
                                        borderRadius: "4px",
                                        border: "1px solid #ddd",
                                      }}
                                    />
                                    <div className="mt-2 flex gap-2">
                                      <Button
                                        type="primary"
                                        onClick={handleSaveReplyEdit}
                                        disabled={isSavingReplyEdit}
                                      >
                                        {isSavingReplyEdit
                                          ? "Saving..."
                                          : "Save"}
                                      </Button>
                                      <Button
                                        type="default"
                                        onClick={() => setEditingReply(null)}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-600">
                                    {reply.content}
                                  </p>
                                )}
                              </p>
                              <p className="text-sm text-gray-600">
                                <ComDateConverter time>
                                  {reply.createAt}
                                </ComDateConverter>
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
